/**
 * Live authenticated Search and Fandom projections. Results preserve canonical
 * catalog identity while every Work card is backed by current readable data.
 */
import { z } from "zod";
import { canonicalIdSchema } from "./content.ts";
import { homeWorkCardSchema } from "./home.ts";
import { sourceLanguageSchema } from "./work.ts";

const labelSchema = z.string().trim().min(1);

export const discoveryWorkCardSchema = homeWorkCardSchema.omit({
  subscriptionReason: true
});

export const searchMatchSchema = z
  .object({
    kind: z.enum(["fandom", "ship", "character", "trope", "work", "creator"]),
    id: canonicalIdSchema,
    label: labelSchema
  })
  .strict();

export const searchResponseSchema = z
  .object({
    displayLanguage: sourceLanguageSchema,
    query: labelSchema,
    results: z
      .array(
        z
          .object({
            match: searchMatchSchema,
            work: discoveryWorkCardSchema
          })
          .strict()
      )
      .max(100),
    nextCursor: z.string().min(1).nullable()
  })
  .strict();

export const fandomResponseSchema = z
  .object({
    displayLanguage: sourceLanguageSchema,
    fandom: z
      .object({
        id: canonicalIdSchema,
        name: labelSchema
      })
      .strict(),
    works: z.array(discoveryWorkCardSchema).max(60)
  })
  .strict();

export type DiscoveryWorkCard = z.infer<typeof discoveryWorkCardSchema>;
export type SearchResponse = z.infer<typeof searchResponseSchema>;
export type FandomResponse = z.infer<typeof fandomResponseSchema>;
